import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { jobService } from '../../services/jobService';
import { ConvertBillsToInvoiceData } from '../../types/job';
import { Job } from '../../types/job';
import { formatCurrency } from '../../utils/formatters';

interface ConvertToInvoiceFormProps {
  job: Job;
  onSuccess: () => void;
  onCancel: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

export const ConvertToInvoiceForm: React.FC<ConvertToInvoiceFormProps> = ({ job, onSuccess, onCancel }) => {
  console.log('ConvertToInvoiceForm rendering with job:', job);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ConvertBillsToInvoiceData>({
    invoiceNumber: '', issueDate: new Date().toISOString().split('T')[0], dueDate: '', notes: '',
  });
  const [errors, setErrors] = useState<string[]>([]);

  const { data: jobDetails } = useQuery({
    queryKey: ['job', job.id],
    queryFn: () => jobService.getJob(job.id),
  });

  const convertMutation = useMutation({
    mutationFn: (data: ConvertBillsToInvoiceData) => jobService.convertBillsToInvoice(job.id, data),
    onSuccess: (_result) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.response?.data?.message || 'Failed to convert bills to invoice']);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    if (!formData.dueDate) { setErrors(['Due date is required']); return; }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { invoiceNumber, ...dataWithoutInvoiceNumber } = formData;
    convertMutation.mutate(dataWithoutInvoiceNumber);
  };

  const convertibleBills = jobDetails?.bills?.filter(bill => bill.status !== 'CANCELLED') || [];
  const totalAmount = convertibleBills.reduce((sum, bill) => sum + bill.total, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', zIndex: 9999, background: 'rgba(4,4,10,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 16px 40px' }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, width: '100%', maxWidth: 720, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>Convert Job Bills to Invoice</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#6b6a5c', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {errors.length > 0 && (
            <div style={{ background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 4, padding: 12 }}>
              <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a' }}>
                {errors.map((error, index) => <li key={index}>{error}</li>)}
              </ul>
            </div>
          )}

          {/* Job Information */}
          <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '14px 16px' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 12 }}>Job Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['Job', `${job.jobNumber} - ${job.jobName}`],
                ['Customer', job.customer?.name],
                ['Total Bills', convertibleBills.length],
                ['Total Amount', formatCurrency(totalAmount)],
              ].map(([k, v]) => (
                <div key={String(k)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                  <span style={{ color: '#6b6a5c' }}>{k}: </span>{v}
                </div>
              ))}
            </div>
          </div>

          {/* Bills to be Converted */}
          {convertibleBills.length > 0 && (
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 10 }}>Bills to be Converted</div>
              <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '8px 14px', maxHeight: 160, overflowY: 'auto' }}>
                {convertibleBills.map((bill) => (
                  <div key={bill.id} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', padding: '4px 0', borderBottom: '1px solid rgba(239,232,213,0.5)' }}>
                    <span>{bill.billNumber} — {bill.vendor?.name}</span>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#fbbf24' }}>{formatCurrency(bill.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {convertibleBills.length === 0 && (
            <div style={{ background: 'rgba(239,232,213,0.5)', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, padding: 14 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                No convertible bills found. Only non-cancelled bills can be converted.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Invoice Number <span style={{ color: '#6b6a5c', fontFamily: "'Inter', sans-serif", fontSize: 10, textTransform: 'none', letterSpacing: 0 }}>(Auto-generated)</span></label>
                <input type="text" value="Will be auto-generated" readOnly style={{ ...inputStyle, background: '#faf7ee', color: '#6b6a5c', cursor: 'default' }} />
              </div>
              <div>
                <label style={labelStyle}>Issue Date *</label>
                <input type="date" value={formData.issueDate} onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))} required style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Due Date *</label>
              <input type="date" value={formData.dueDate} onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))} required style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea rows={3} value={formData.notes || ''} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Additional notes for this invoice..." style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} />
            </div>

            {/* Conversion Summary */}
            <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: '14px 16px' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 10 }}>Conversion Summary</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ margin: 0 }}>This will create a new invoice with {convertibleBills.length} line items.</p>
                <p style={{ margin: 0 }}>Total: <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>{formatCurrency(totalAmount)}</span></p>
                <p style={{ margin: 0, fontSize: 11, color: '#8a8778' }}>Original bills remain with a conversion note.</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
              <button type="button" onClick={onCancel} style={{ padding: '10px 24px', background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer' }}>Cancel</button>
              <button type="submit" disabled={convertMutation.isPending || convertibleBills.length === 0} style={{ padding: '10px 24px', background: (convertMutation.isPending || convertibleBills.length === 0) ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: (convertMutation.isPending || convertibleBills.length === 0) ? '#c9c2ab' : '#1c2033', cursor: (convertMutation.isPending || convertibleBills.length === 0) ? 'not-allowed' : 'pointer' }}>
                {convertMutation.isPending ? 'Converting...' : 'Convert to Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
